"use client";

import { useEffect, useRef } from "react";
import { adsConfig, type AdPosition } from "@/lib/ads";
import { cn } from "@/lib/utils";
declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

// Fixed sizes so the page never jumps when an ad loads.
const SIZE: Record<AdPosition, {
  box: string;
  label: string;
}> = {
  left: {
    box: "h-[600px] w-[160px]",
    label: "160 × 600"
  },
  right: {
    box: "h-[600px] w-[160px]",
    label: "160 × 600"
  },
  inline: {
    box: "h-[100px] w-full max-w-[728px]",
    label: "responsive banner"
  }
};

/** One Google AdSense slot, or a grey "Ad slot" placeholder when
 *  NEXT_PUBLIC_ADSENSE_CLIENT is empty (always on localhost).
 *  Never place inside a tool workspace or near Download/Save buttons. */
export function AdSlot({
  position,
  className
}: {
  position: AdPosition;
  className?: string;
}) {
  const {
    client,
    slots
  } = adsConfig;
  const insRef = useRef<HTMLModElement>(null);
  const size = SIZE[position];
  useEffect(() => {
    const ins = insRef.current;
    // Rails are hidden with CSS below 1280 px; AdSense can't fill a 0-wide slot.
    if (!client || !ins || ins.offsetWidth === 0) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Ad blockers or a slow network: leave the reserved space empty.
    }
  }, [client]);
  return <aside aria-label="Advertisement" data-ad-position={position} className={cn(position === "inline" && "w-full", className)}>
      {client ? <ins ref={insRef} className={cn("adsbygoogle mx-auto block", size.box)} data-ad-client={client} data-ad-slot={slots[position]} {...position === "inline" && {
      "data-full-width-responsive": "true"
    }} /> : <div className={cn("mx-auto flex flex-col items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-line-strong bg-muted text-center text-[13px] text-muted-foreground", size.box)}>
          <span className="font-semibold tracking-[0.08em] uppercase">
            Ad slot
          </span>
          <span>{size.label}</span>
        </div>}
      <p className="mt-1.5 text-center text-xs text-muted-foreground">
        Advertisement
      </p>
    </aside>;
}